'use client'

import classNames from 'classnames'
import {
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react'
import styles from './ClientModalDialog.module.scss'

export type ClientModalDialogProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode
  closeOnEscape?: boolean
  closeOnOverlayClick?: boolean
  isOpen?: boolean
  onClose?: () => void
}

export const ClientModalDialog = ({
  children,
  className,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  isOpen: defaultIsOpen = true,
  onClose,
  ...rest
}: ClientModalDialogProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen)

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onClose?.()
  }, [onClose])

  const handleContainerClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (closeOnOverlayClick === false) {
        return
      }

      if (containerRef.current === event.currentTarget) {
        handleClose()
      }
    },
    [closeOnOverlayClick, handleClose],
  )

  const handleContainerKeyDown: React.KeyboardEventHandler<HTMLDivElement> =
    useCallback(
      (event) => {
        if (closeOnEscape === false) {
          return
        }

        if (event.key === 'Escape') {
          handleClose()
        }
      },
      [closeOnEscape, handleClose],
    )

  if (isOpen) {
    const finalClassName = classNames(styles['client-modal-dialog'], className)

    return (
      <div
        {...rest}
        ref={containerRef}
        onClick={handleContainerClick}
        onKeyDown={handleContainerKeyDown}
        className={finalClassName}
      >
        {children}
      </div>
    )
  }

  return null
}
